import { LineItem } from "../../interfaces/LineItem";
import { ReportUseGroup } from "../../interfaces/EstimateUseGroup";
import { FloorGroup } from "../../interfaces/AreaSheet";
import { splitLineItemsByDimension } from "../utils/lineItems";
import { getNamedRangeFromWorksheet, setPrintAreaToUsedRange } from "../workbook";
import { getEstimateReportData } from "../workbook";
import { ValidationError } from "../../errors";
import { alternatePassesFilters, filterLineItemsByAlternates, hasAlternateFilters } from "./helpers/alternateFilters";
import { BLANK_LABEL, codeLabel, getReportOptions, updateReportDate } from "./helpers/common";
import { addGroupingMetaData } from "./helpers/groupings";
import { RowFormat, printFormattedData } from "./helpers/print";
import {
  calculatedRangeNames,
  columnNames,
  rangeNames,
  rowNames,
  summableCalculatedColumnNames,
} from "./helpers/rangeNames";
import { cacheableData, worksheetCache } from "../workbook/cache";
import { getOverlineQuantities, getProcessedEstimate } from "../datasources/estimate";
import { transformUseGroups } from "../datasources/useGroups";
import { fetchEstimateExport } from "../../api/estimates";
import { track } from "../../clients/mixpanel";
import { ColumnNameToIndexMap } from "./helpers/common";
import { getEstimateGroupings } from "./helpers/groupings";
import { createColumnNameToIndexMap } from "./helpers/print";
import { markupsReportArray } from "./sov";
import { UseGroupColumnInfo } from "./helpers/useGroups";
import { FloorGroupColumnInfo } from "./helpers/floorGroups";
import {
  CrossTabSortField,
  aliasCrossTabColumn,
  crossTabColumnCode,
  fillCrossTabBlocks,
  parseCrossTabColumns,
} from "./helpers/crossTab";
import { styleCostPerRows } from "./helpers/costDenominators";
import { lookupOverlineQuantity } from "../datasources/overlineTotals";
import { BLANK_ALTERNATE_LABEL } from "../../interfaces/Alternate";
import { reportLineItemsWithAllocatedMarkups } from "../utils/markups";
import { OverlineQuantitiesMap } from "../../interfaces/OverlineTotals";

export interface BaseEstimateReportOptions extends cacheableData {
  groupByProps: string[];
  subtotalsInHeader: boolean;
  groupRows: boolean;
  splitByUse: boolean;
  includeAssemblyItems: boolean;
  freezeGroupLevels: boolean;
  onlyLevelOneSubtotals: boolean;
  showCodes: boolean;
  hideZeroCostItems: boolean;
  /** What fills the `\cross_tab` block; see `parseCrossTabColumns`. */
  crossTabColumns: string;
  /** Prefix cross-tab column headers with their code. */
  showCodesColumns: boolean;
}

export interface EstimateReportOptions extends BaseEstimateReportOptions {
  noGrouping: boolean;
  allocateMarkups: boolean;
  filterByUseGroup: string;
  hideLineItemDetails: boolean;
}

export interface AlternatesReportOptions extends BaseEstimateReportOptions {
  excludeAccepted: boolean;
  excludeRejected: boolean;
  filterByAlternate: string[];
  filterByType: string;
}

interface EstimateReportArrayArgs {
  data: LineItem[];
  columnMap: ColumnNameToIndexMap;
  overlineQuantitiesMap: OverlineQuantitiesMap;
  childLineItems: LineItem[];
  useGroups?: ReportUseGroup[];
  floorGroups?: FloorGroup[];
  options: EstimateReportOptions | AlternatesReportOptions;
  useGroupColumnIndices?: UseGroupColumnInfo;
  floorGroupColumnIndices?: FloorGroupColumnInfo;
  costDenominatorsRowIndex?: number;
  costPerColumnsRequested?: boolean;
  /** Set when the `\cross_tab` block shows a sort field or MF/UF level. */
  crossTabSortField?: CrossTabSortField;
}

export const estimateDefaultOptions = {
  noGrouping: false,
  groupRows: true,
  subtotalsInHeader: true,
  splitByUse: false,
  includeAssemblyItems: false,
  freezeGroupLevels: false,
  onlyLevelOneSubtotals: false,
  allocateMarkups: true,
  filterByUseGroup: "",
  showCodes: true,
  showCodesColumns: true,
  hideLineItemDetails: false,
  hideZeroCostItems: false,
  crossTabColumns: "",
} as EstimateReportOptions;

export const alternatesDefaultOptions = {
  groupRows: true,
  subtotalsInHeader: true,
  splitByUse: false,
  includeAssemblyItems: false,
  freezeGroupLevels: false,
  onlyLevelOneSubtotals: false,
  showCodes: true,
  showCodesColumns: true,
  hideZeroCostItems: false,
  excludeAccepted: false,
  excludeRejected: false,
  filterByAlternate: [] as string[],
  filterByType: "",
  crossTabColumns: "",
  // allocateMarkups: true,
} as AlternatesReportOptions;

export const fetchEstimateItemsForReport = async (estimateIdParam?: string): Promise<Record<string, any>[]> => {
  let estimateId = estimateIdParam;
  if (!estimateId) {
    const reportData = (await getEstimateReportData()) as any[];
    estimateId = reportData[0]?.estimate_id as string | undefined;
  }
  if (!estimateId) return [];
  const result = await fetchEstimateExport(estimateId);
  if (!result.success) throw result.error;
  if (!result.data) return [];
  return result.data.line_items;
};

export const generateEstimateReport = async (worksheet: Excel.Worksheet) => {
  const startTime = performance.now();
  const reportData = (await getEstimateReportData()) as any[];
  const estimateId = reportData[0]?.estimate_id as string | undefined;
  if (!estimateId) {
    throw new ValidationError("Load an estimate before generating a report.");
  }

  const processed = await getProcessedEstimate(estimateId);
  if (!processed) {
    throw new ValidationError("Load an estimate before generating a report.");
  }

  // Get options early to check for filterByUseGroup filter
  const options = (await getReportOptions(worksheet, estimateDefaultOptions)) as EstimateReportOptions;

  let lineItems = processed.baseBidItems.filter((item) => item.type === "line_item");
  const floorGroups = processed.estimateData.floor_groups || [];
  const useGroups = transformUseGroups(processed.estimateData.estimate.stats.useGroups || [], floorGroups);
  const overlineQuantitiesMap = await getOverlineQuantities(processed);

  useGroups.sort((a, b) => a.order - b.order);
  floorGroups.sort((a, b) => a.label.localeCompare(b.label));

  const totalGsf = useGroups.filter((ug) => !ug.exclude_area).reduce((sum, ug) => sum + (ug.gsf || 0), 0);
  const gsfRange = await getNamedRangeFromWorksheet(worksheet, rangeNames.gsf);
  if (gsfRange) {
    gsfRange.values = [[totalGsf]];
  }

  const markups = processed.baseBidItems.filter(
    (item) => item.type === "markup" || (!options.allocateMarkups && item.type === "atl_markup")
  );
  const crossTabColumns = parseCrossTabColumns(options.crossTabColumns);
  const {
    crossTabSortField,
    useGroupColumnIndices,
    floorGroupColumnIndices,
    costDenominatorsRowIndex,
    costPerColumnsRequested,
  } = await fillCrossTabBlocks({
    worksheet,
    processed,
    dataRowName: rowNames.lineItems,
    lineItems,
    markups,
    useGroups,
    floorGroups,
    crossTabColumns,
    showCodesColumns: options.showCodesColumns,
    overlineQuantities: overlineQuantitiesMap,
    isSOV: false,
  });

  const columnMap = await createColumnNameToIndexMap(worksheet);
  aliasCrossTabColumn(columnMap, crossTabColumns);

  if (options.noGrouping) {
    options.groupByProps = [];
    options.hideLineItemDetails = false;
  } else if (!options.freezeGroupLevels || !options.groupByProps?.length) {
    options.groupByProps = await getEstimateGroupings(estimateId);
  }

  const childLineItems = options.includeAssemblyItems ? processed.childItems : [];
  const { dataArray: lineItemDataArray, formatList: lineItemFormatList } = estimateReportArray({
    data: lineItems,
    columnMap,
    overlineQuantitiesMap,
    childLineItems,
    useGroups: useGroups || [],
    floorGroups: floorGroups || [],
    options,
    useGroupColumnIndices,
    floorGroupColumnIndices,
    costDenominatorsRowIndex,
    costPerColumnsRequested,
    crossTabSortField,
  });
  const startColumnIndex = columnMap.startColumnIndex;
  await printFormattedData({
    dataArray: lineItemDataArray,
    formatList: lineItemFormatList,
    worksheet,
    insertAtRangeName: rowNames.lineItems,
    startColumnIndex,
    options,
    columnMap,
  });

  await styleCostPerRows(
    worksheet,
    rowNames.lineItems,
    lineItemFormatList,
    costPerColumnOffsets(columnMap, useGroups, floorGroups, crossTabSortField).map(
      (offset) => startColumnIndex + offset
    )
  );

  // cache last groupByProps for the report
  if (!options.noGrouping) {
    await worksheetCache.setOptions(worksheet, { groupByProps: options.groupByProps });
  }

  // Print markups if the named range for markups exists in the worksheet
  if (await getNamedRangeFromWorksheet(worksheet, rowNames.markups)) {
    const rowMap = costDenominatorsRowIndex != null ? { [rowNames.costDenominators]: costDenominatorsRowIndex } : {};
    const { dataArray: markupDataArray } = markupsReportArray(
      markups,
      columnMap,
      rowMap,
      useGroups,
      floorGroups,
      undefined,
      crossTabSortField
    );
    await printFormattedData({
      dataArray: markupDataArray,
      //formatList: markupFormatList, actually better to let the user format without styles
      worksheet,
      insertAtRangeName: rowNames.markups,
      startColumnIndex,
    });
  }

  //if the report grouping named range exists, add the grouping metadata
  await addGroupingMetaData(worksheet, options.noGrouping ? ["None"] : options.groupByProps);
  await updateReportDate(worksheet);
  await setPrintAreaToUsedRange(worksheet);

  track("excel.createReport.estimate", {
    options: options,
    loadTime: (performance.now() - startTime) / 1000,
  });
};

export const fetchAlternateItemsForReport = async (estimateIdParam?: string): Promise<Record<string, any>[]> => {
  let estimateId = estimateIdParam;
  if (!estimateId) {
    const reportData = (await getEstimateReportData()) as any[];
    estimateId = reportData[0]?.estimate_id as string | undefined;
  }
  if (!estimateId) return [];
  const processed = await getProcessedEstimate(estimateId);
  if (!processed) return [];
  return processed.alternateItems.filter((item) => item.type === "line_item") as unknown as Record<string, any>[];
};

export const generateAlternatesItemsReport = async (worksheet: Excel.Worksheet) => {
  const startTime = performance.now();
  const options = (await getReportOptions(worksheet, alternatesDefaultOptions)) as AlternatesReportOptions;
  // Ensure alternates report uses the same splitByUse setting as the estimate report
  const estimateOptions = (await getReportOptions(worksheet, estimateDefaultOptions)) as EstimateReportOptions;
  options.splitByUse = estimateOptions.splitByUse;

  const reportData = (await getEstimateReportData()) as any[];
  const estimateId = reportData[0]?.estimate_id as string | undefined;
  if (!estimateId) {
    throw new ValidationError("Load an estimate before generating a report.");
  }

  const processed = await getProcessedEstimate(estimateId);
  if (!processed || processed.alternateItems.length === 0) {
    throw new ValidationError("Load an estimate before generating a report.");
  }

  const alternates = (processed.estimateData.alternates || []) as unknown as LineItem[];
  let lineItems = processed.alternateItems.filter((item) => item.type === "line_item");

  const hasFilters = hasAlternateFilters(options);

  if (hasFilters) {
    const filteredAlternates = alternates.filter((alt) => alternatePassesFilters(alt, options));
    lineItems = filterLineItemsByAlternates(lineItems, filteredAlternates);
  }

  const floorGroups = processed.estimateData.floor_groups || [];
  const useGroups = transformUseGroups(processed.estimateData.estimate.stats.useGroups || [], floorGroups);
  const overlineQuantitiesMap = await getOverlineQuantities(processed);

  useGroups.sort((a, b) => a.order - b.order);
  floorGroups.sort((a, b) => a.label.localeCompare(b.label));

  const totalGsf = useGroups.filter((ug) => !ug.exclude_area).reduce((sum, ug) => sum + (ug.gsf || 0), 0);
  const gsfRange = await getNamedRangeFromWorksheet(worksheet, rangeNames.gsf);
  if (gsfRange) {
    gsfRange.values = [[totalGsf]];
  }

  const markups = processed.baseBidItems.filter(
    (item) => item.type === "markup" || (!options.allocateMarkups && item.type === "atl_markup")
  );
  const crossTabColumns = parseCrossTabColumns(options.crossTabColumns);
  const {
    crossTabSortField,
    useGroupColumnIndices,
    floorGroupColumnIndices,
    costDenominatorsRowIndex,
    costPerColumnsRequested,
  } = await fillCrossTabBlocks({
    worksheet,
    processed,
    dataRowName: rowNames.alternateItems,
    lineItems,
    markups,
    useGroups,
    floorGroups,
    crossTabColumns,
    showCodesColumns: options.showCodesColumns,
    overlineQuantities: overlineQuantitiesMap,
    isSOV: false,
  });

  const columnMap = await createColumnNameToIndexMap(worksheet);
  aliasCrossTabColumn(columnMap, crossTabColumns);
  if (!options.freezeGroupLevels || !options.groupByProps?.length) {
    options.groupByProps = await getEstimateGroupings(estimateId);
  }

  // Insert 'alternate' as the top-level grouping
  if (!options.groupByProps.includes("alternate")) {
    options.groupByProps.unshift("alternate");
  }

  const childLineItems = options.includeAssemblyItems ? processed.childItems : [];
  const { dataArray: lineItemDataArray, formatList: lineItemFormatList } = estimateReportArray({
    data: lineItems,
    columnMap,
    overlineQuantitiesMap,
    childLineItems,
    useGroups: useGroups || [],
    floorGroups: floorGroups || [],
    options,
    useGroupColumnIndices,
    floorGroupColumnIndices,
    costDenominatorsRowIndex,
    costPerColumnsRequested,
    crossTabSortField,
  });
  const startColumnIndex = columnMap.startColumnIndex;

  await printFormattedData({
    dataArray: lineItemDataArray,
    formatList: lineItemFormatList,
    worksheet,
    insertAtRangeName: rowNames.alternateItems,
    startColumnIndex,
    options,
    columnMap,
  });

  await styleCostPerRows(
    worksheet,
    rowNames.alternateItems,
    lineItemFormatList,
    costPerColumnOffsets(columnMap, useGroups, floorGroups, crossTabSortField).map(
      (offset) => startColumnIndex + offset
    )
  );

  // cache last groupByProps for the report
  await worksheetCache.setOptions(worksheet, { groupByProps: options.groupByProps });

  // Print markups if the named range for markups exists in the worksheet
  if (await getNamedRangeFromWorksheet(worksheet, rowNames.markups)) {
    const rowMap = costDenominatorsRowIndex != null ? { [rowNames.costDenominators]: costDenominatorsRowIndex } : {};
    const { dataArray: markupDataArray } = markupsReportArray(
      markups,
      columnMap,
      rowMap,
      useGroups,
      floorGroups,
      undefined,
      crossTabSortField
    );
    await printFormattedData({
      dataArray: markupDataArray,
      //formatList: markupFormatList, actually better to let the user format without styles
      worksheet,
      insertAtRangeName: rowNames.markups,
      startColumnIndex,
    });
  }

  //if the report grouping named range exists, add the grouping metadata
  await addGroupingMetaData(worksheet, options.groupByProps);
  await updateReportDate(worksheet);
  await setPrintAreaToUsedRange(worksheet);

  track("excel.createReport.alternatesEstimates", {
    options: options,
    loadTime: (performance.now() - startTime) / 1000,
  });
};

/** Offsets, within the report, of every cost-per column the row builder writes. */
const costPerColumnOffsets = (
  columnMap: ColumnNameToIndexMap,
  useGroups: ReportUseGroup[],
  floorGroups: FloorGroup[],
  crossTabSortField?: CrossTabSortField
): number[] => {
  const firstCrossTabColumnIndex = columnMap[columnNames.crossTab];
  const crossTabOffsets =
    crossTabSortField && firstCrossTabColumnIndex !== undefined
      ? crossTabSortField.columns.flatMap((column) =>
          column.costPer ? [firstCrossTabColumnIndex + column.costPer.offset] : []
        )
      : [];
  return [
    ...useGroups.flatMap((ug) => [columnMap[`${ug.id}_gsf`], columnMap[`${ug.id}_unit`]]),
    ...floorGroups.map((fg) => columnMap[`${fg.id}_gsf`]),
    ...crossTabOffsets,
  ].filter((offset): offset is number => offset !== undefined);
};

const estimateReportArray = ({
  data,
  columnMap,
  overlineQuantitiesMap,
  childLineItems = [],
  useGroups = [],
  floorGroups = [],
  options,
  useGroupColumnIndices = {},
  floorGroupColumnIndices = {},
  costDenominatorsRowIndex,
  costPerColumnsRequested = false,
  crossTabSortField,
}: EstimateReportArrayArgs): { dataArray: (string | number | null)[][]; formatList: RowFormat[] } => {
  const {
    onlyLevelOneSubtotals,
    allocateMarkups,
    groupByProps,
    addBlankRowBetweenGroups,
    subtotalsInHeader,
    showCodes,
    filterByUseGroup,
    hideLineItemDetails,
    hideZeroCostItems,
  } = options;
  let splitByUse = options.splitByUse;

  const filterByUseGroupName = filterByUseGroup ? useGroups.find((ug) => ug.id === filterByUseGroup)?.name : null;

  // When a specific use group is selected, enable splitByUse to properly allocate multi-use items
  if (filterByUseGroupName) {
    splitByUse = true;
  }

  let sortedData = data.slice().sort((a, b) => {
    const orderComparison = (a.order ?? "").localeCompare(b.order ?? "", undefined, { numeric: true });
    if (orderComparison !== 0) return orderComparison;
    return a.name.localeCompare(b.name);
  });

  if (allocateMarkups) {
    sortedData = reportLineItemsWithAllocatedMarkups(sortedData);
    childLineItems = reportLineItemsWithAllocatedMarkups(childLineItems);
  }

  const formatList: RowFormat[] = [];
  let maxColumnIndex = Math.max(...Object.values(columnMap));
  const indentColumn = Math.min(...Object.values(columnMap));
  const useGroupRangeName = columnNames.useGroups;
  const firstUseGroupColumnIndex = columnMap?.[useGroupRangeName];
  const useGroupsRequested = firstUseGroupColumnIndex !== undefined;

  const floorGroupRangeName = columnNames.floorGroups;
  const firstFloorGroupColumnIndex = columnMap?.[floorGroupRangeName];
  const floorGroupsRequested = firstFloorGroupColumnIndex !== undefined;

  // Extend column map with useGroup totals and cost per columns
  if (useGroupsRequested) {
    // Add useGroup column indices directly to the original columnMap, similar to SOV report
    useGroups.forEach((useGroup) => {
      const columnInfo = useGroupColumnIndices[useGroup.id];
      if (columnInfo) {
        // Convert absolute Excel column indices to relative indices for the columnMap
        columnMap[useGroup.id] = columnInfo.total - columnMap.startColumnIndex;
        // Add gsf and unit columns to the map if they exist
        if (columnInfo.gsf !== undefined) {
          columnMap[`${useGroup.id}_gsf`] = columnInfo.gsf - columnMap.startColumnIndex;
        }
        if (columnInfo.unit !== undefined) {
          columnMap[`${useGroup.id}_unit`] = columnInfo.unit - columnMap.startColumnIndex;
        }
      }
    });
    // Now columnMap contains all the necessary column indices including use groups
    maxColumnIndex = Math.max(...Object.values(columnMap));
  }

  // Extend column map with floorGroup totals and cost per columns
  if (floorGroupsRequested) {
    // Add floorGroup column indices directly to the original columnMap, similar to use groups
    floorGroups.forEach((floorGroup) => {
      const columnInfo = floorGroupColumnIndices[floorGroup.id];
      if (columnInfo) {
        // Convert absolute Excel column indices to relative indices for the columnMap
        columnMap[floorGroup.id] = columnInfo.total - columnMap.startColumnIndex;
        // Add gsf column to the map if it exists
        if (columnInfo.gsf !== undefined) {
          columnMap[`${floorGroup.id}_gsf`] = columnInfo.gsf - columnMap.startColumnIndex;
        }
      }
    });
    // Now columnMap contains all the necessary column indices including floor groups
    maxColumnIndex = Math.max(...Object.values(columnMap));
  }

  // The `\cross_tab` block's cells when it shows a sort field or MF/UF level: each code's cost cell
  // and, beside it, its cost-per cell. Offsets are within the report, like the column map's.
  const firstCrossTabColumnIndex = columnMap[columnNames.crossTab];
  const crossTabCodeKey = crossTabSortField?.codeKey ?? "";
  const crossTabCells =
    crossTabSortField && firstCrossTabColumnIndex !== undefined
      ? crossTabSortField.columns.map((column) => ({
          code: column.code,
          total: firstCrossTabColumnIndex + column.total,
          costPer: column.costPer ? firstCrossTabColumnIndex + column.costPer.offset : undefined,
        }))
      : [];
  if (crossTabSortField && firstCrossTabColumnIndex !== undefined) {
    maxColumnIndex = Math.max(maxColumnIndex, firstCrossTabColumnIndex + crossTabSortField.headers.length - 1);
    // The column map records only the block's first column. The print clears template-row formulas
    // only in columns the map claims, and the resize copies the first column's old formulas into the
    // others, so claim every cell of the block or a header row left blank shows =SUBTOTAL(9,#REF!).
    crossTabSortField.headers.forEach((_, offset) => {
      columnMap[`${columnNames.crossTab}_${offset}`] = firstCrossTabColumnIndex + offset;
    });
  }
  const crossTabCostPerFormula =
    costPerColumnsRequested && costDenominatorsRowIndex !== undefined
      ? `=IFERROR(RC[-1]/R${costDenominatorsRowIndex + 1}C,0)`
      : undefined;

  // Writes a row's cross-tab cells: `cost` for each code's cost cell and `costPer` beside it
  const writeCrossTabCells = (
    row: (string | number | null)[],
    cost: (code: string) => string | number,
    costPer: string | undefined
  ) => {
    crossTabCells.forEach((cell) => {
      row[cell.total] = cost(cell.code);
      if (cell.costPer !== undefined && costPer !== undefined) row[cell.costPer] = costPer;
    });
  };

  // An item's cost lands under its own code; every other code's cell reads 0, like the use-group cells
  const crossTabItemCost = (item: LineItem) => {
    const itemCode = crossTabColumnCode(item, crossTabCodeKey);
    const total = Number(item.total) || 0;
    return (code: string) => (code === itemCode ? total : 0);
  };

  const splitLineItems = (items: LineItem[]): LineItem[] => {
    // Early returns
    if (!splitByUse) return items;
    if (!groupByProps.includes("floor_group") && !groupByProps.includes("use_group") && !filterByUseGroupName) {
      return items;
    }

    let processedItems = items;

    // Step 1: Split by floor_group
    if (groupByProps.includes("floor_group") && floorGroups.length > 0) {
      processedItems = splitLineItemsByDimension(
        processedItems,
        floorGroups,
        "floor_group",
        "_$$",
        (group) => group.label
      );
    }

    // Step 2: Split by use_group (if grouping by use_group OR filtering by specific use group)
    if ((groupByProps.includes("use_group") || filterByUseGroupName) && useGroups.length > 0) {
      processedItems = splitLineItemsByDimension(
        processedItems,
        useGroups,
        "use_group",
        "_$",
        (group) => group.name,
        (g, item) => {
          // Only filter by floor_group if we split by building (floor_group is in groupByProps)
          if (groupByProps.includes("floor_group") && floorGroups.length > 0) {
            return g.floor_group === item.floor_group;
          }
          return true;
        }
      );
    }

    return processedItems;
  };

  let processedData = splitLineItems(sortedData);
  let processedChildItems = splitLineItems(childLineItems);

  if (filterByUseGroupName) {
    // Filter split results to only show the selected use group if one is specified
    processedData = processedData.filter((item) => item.use_group === filterByUseGroupName);
    processedChildItems = processedChildItems.filter((item) => item.use_group === filterByUseGroupName);
  }

  const isZeroCost = (item: LineItem) => item.total === 0;

  if (hideZeroCostItems) {
    processedChildItems = processedChildItems.filter((item) => !isZeroCost(item));
  }

  const assemblyLookupKey = (item: LineItem, assemblyId: string) => {
    let lookupKey = assemblyId;
    // When splitByUse is enabled and floor_group is in groupByProps, include floor_group
    if (splitByUse && groupByProps.includes("floor_group")) {
      lookupKey = lookupKey + item.floor_group;
    }
    // When use_group is in groupByProps, also include use_group
    if (splitByUse && groupByProps.includes("use_group")) {
      lookupKey = lookupKey + item.use_group;
    }
    return lookupKey;
  };

  const childItemLookup: Record<string, LineItem[]> = processedChildItems.reduce(
    (acc: Record<string, LineItem[]>, item: LineItem) => {
      if (!item.parent) return acc;
      const tempLookup = assemblyLookupKey(item, item.parent);
      acc[tempLookup] = acc[tempLookup] || [];
      acc[tempLookup].push(item);
      return acc;
    },
    {}
  );

  Object.keys(childItemLookup).forEach((key) => {
    childItemLookup[key].sort((a, b) => {
      const orderComparison = parseFloat(a.order) - parseFloat(b.order);
      if (orderComparison !== 0) return orderComparison;
      return a.name.localeCompare(b.name);
    });
  });

  if (hideZeroCostItems) {
    processedData = processedData.filter(
      (item) => !isZeroCost(item) || childItemLookup[assemblyLookupKey(item, item.id)]
    );
  }

  const quantityColIndex = columnMap.quantity;
  const uomColIndex = columnMap.uom;
  const unitCostColIndex = columnMap.total_uc;
  const totalColIndex = columnMap.total;
  const costPerGSFColumnIndex = columnMap?.[columnNames.costPerGSF];
  const summableColumns = summableCalculatedColumnNames
    .filter((name) => columnMap[name] !== undefined)
    .map((name) => ({ name, index: columnMap[name] }));

  // Keep the "… Subtotal" label left of every value so it never lands between two subtotal columns.
  const subtotalValueColumnIndices = [
    totalColIndex,
    costPerGSFColumnIndex,
    ...summableColumns.map(({ index }) => index),
    ...useGroups.flatMap((ug) => [columnMap[ug.id], columnMap[`${ug.id}_gsf`], columnMap[`${ug.id}_unit`]]),
    ...floorGroups.flatMap((fg) => [columnMap[fg.id], columnMap[`${fg.id}_gsf`]]),
    ...crossTabCells.flatMap((cell) => [cell.total, cell.costPer]),
  ].filter((index) => index !== undefined);
  const subtotalLabelColumnIndex = subtotalValueColumnIndices.length
    ? Math.max(Math.min(...subtotalValueColumnIndices) - 1, 0)
    : 0;

  // Recursive function to group data
  const groupData = (
    items: LineItem[],
    level: number,
    lineage: Array<{ [key: string]: string }>
  ): (string | number | null)[][] => {
    if (level >= groupByProps.length) {
      // Base case: last grouping level — skip all line items when hideLineItemDetails is on
      if (hideLineItemDetails) return [];
      return items.flatMap((item) => {
        const row: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
        Object.keys(columnMap).forEach((key) => {
          // Skip use group and floor group columns as they're handled separately
          const isUseGroupColumn = useGroups.some(
            (ug) => key === ug.id || key === `${ug.id}_gsf` || key === `${ug.id}_unit`
          );
          const isFloorGroupColumn = floorGroups.some((fg) => key === fg.id || key === `${fg.id}_gsf`);
          if (!isUseGroupColumn && !isFloorGroupColumn) {
            if (key in item) {
              row[columnMap[key]] = item[key];
            } else if (key in calculatedRangeNames) {
              row[columnMap[key]] = calculatedRangeNames[key](item);
            }
          }
        });

        // use formulas in total column
        const unitCostOffset = unitCostColIndex - totalColIndex;
        const quantityOffset = quantityColIndex - totalColIndex;
        const hasUnitCost = unitCostColIndex !== undefined;
        const hasQuantity = quantityColIndex !== undefined;
        const hasTotal = totalColIndex !== undefined;
        const unitCostVal = hasUnitCost ? row[unitCostColIndex] : null;
        const quantityVal = hasQuantity ? row[quantityColIndex] : null;
        const totalVal = hasTotal ? row[totalColIndex] : null;

        const totalIsText = typeof totalVal === "string" && totalVal !== "";
        const canSetTotal =
          !totalIsText && hasUnitCost && hasQuantity && hasTotal && unitCostVal != null && quantityVal != null;
        const canSetUnitCost =
          !totalIsText &&
          hasUnitCost &&
          hasTotal &&
          hasQuantity &&
          unitCostVal == null &&
          totalVal != null &&
          quantityVal != null;

        if (canSetTotal) {
          row[totalColIndex] = `=IFERROR(RC[${unitCostOffset}]*RC[${quantityOffset}],"")`;
        } else if (canSetUnitCost) {
          row[unitCostColIndex] = `=IFERROR(RC[${-unitCostOffset}]/RC[${quantityColIndex - unitCostColIndex}],"")`;
        }

        if (costPerGSFColumnIndex !== undefined && totalColIndex !== undefined) {
          row[costPerGSFColumnIndex] = `=IFERROR(RC[${totalColIndex - costPerGSFColumnIndex}]/_gsf,"")`;
        }

        if (useGroupsRequested) {
          // Add use group totals and cost per columns
          useGroups.forEach((useGroup) => {
            // When split by use and grouped by floor_group, only include the _$ value
            // if the use group belongs to the current item's floor group
            const useGroupTotal =
              splitByUse && groupByProps.includes("floor_group")
                ? useGroup.floor_group === item.floor_group
                  ? Number(item[`${useGroup.name}_$`]) || 0
                  : 0
                : Number(item[`${useGroup.name}_$`]) || 0;

            // Use the relative column index from columnMap
            row[columnMap[useGroup.id]] = useGroupTotal;

            // Add cost per GSF and unit formulas if denominators are requested
            if (costPerColumnsRequested && costDenominatorsRowIndex !== undefined) {
              if (columnMap[`${useGroup.id}_gsf`] !== undefined) {
                row[columnMap[`${useGroup.id}_gsf`]] = `=IFERROR(RC[-1]/R${costDenominatorsRowIndex + 1}C,0)`;
              }
              if (columnMap[`${useGroup.id}_unit`] !== undefined) {
                const c = columnMap[`${useGroup.id}_gsf`] !== undefined ? -2 : -1;
                row[columnMap[`${useGroup.id}_unit`]] = `=IFERROR(RC[${c}]/R${costDenominatorsRowIndex + 1}C,0)`;
              }
            }
          });
        }

        if (floorGroupsRequested) {
          // Add floor group totals and cost per columns
          floorGroups.forEach((floorGroup) => {
            // When split by use and grouped by floor_group, use the _$$ value directly
            // only if this floor group matches the current item's floor group
            const floorGroupTotal =
              splitByUse && groupByProps.includes("floor_group")
                ? floorGroup.label === item.floor_group
                  ? Number(item[`${floorGroup.label}_$$`]) || 0
                  : 0
                : useGroups
                    .filter((ug) => ug.floor_group === floorGroup.label)
                    .reduce((sum, useGroup) => sum + (Number(item[`${useGroup.name}_$`]) || 0), 0);

            // Use the relative column index from columnMap
            row[columnMap[floorGroup.id]] = floorGroupTotal;

            // Add cost per GSF formula if denominators are requested
            if (costPerColumnsRequested && costDenominatorsRowIndex !== undefined) {
              if (columnMap[`${floorGroup.id}_gsf`] !== undefined) {
                row[columnMap[`${floorGroup.id}_gsf`]] = `=IFERROR(RC[-1]/R${costDenominatorsRowIndex + 1}C,0)`;
              }
            }
          });
        }

        writeCrossTabCells(row, crossTabItemCost(item), crossTabCostPerFormula);

        formatList.push({ style: "item", level, indentColumn, group: Array.from({ length: level }, (_, i) => i + 1) });

        let childRows: (string | number | null)[][] = [];
        const tempLookup = assemblyLookupKey(item, item.id);

        if (childItemLookup[tempLookup]) {
          row[totalColIndex] = `=SUBTOTAL(9, R[1]C:R[${childItemLookup[tempLookup].length}]C)`;

          // Also make use group columns (and their cost per columns) use SUBTOTAL for parent assemblies
          if (useGroupsRequested) {
            useGroups.forEach((useGroup) => {
              row[columnMap[useGroup.id]] = `=SUBTOTAL(9, R[1]C:R[${childItemLookup[tempLookup].length}]C)`;
              if (costPerColumnsRequested && costDenominatorsRowIndex !== undefined) {
                if (columnMap[`${useGroup.id}_gsf`] !== undefined) {
                  row[columnMap[`${useGroup.id}_gsf`]] =
                    `=SUBTOTAL(9, R[1]C:R[${childItemLookup[tempLookup].length}]C)`;
                }
                if (columnMap[`${useGroup.id}_unit`] !== undefined) {
                  row[columnMap[`${useGroup.id}_unit`]] =
                    `=SUBTOTAL(9, R[1]C:R[${childItemLookup[tempLookup].length}]C)`;
                }
              }
            });
          }

          // Also make floor group columns (and their cost per columns) use SUBTOTAL for parent assemblies
          if (floorGroupsRequested) {
            floorGroups.forEach((floorGroup) => {
              row[columnMap[floorGroup.id]] = `=SUBTOTAL(9, R[1]C:R[${childItemLookup[tempLookup].length}]C)`;
              if (
                costPerColumnsRequested &&
                costDenominatorsRowIndex !== undefined &&
                columnMap[`${floorGroup.id}_gsf`] !== undefined
              ) {
                row[columnMap[`${floorGroup.id}_gsf`]] =
                  `=SUBTOTAL(9, R[1]C:R[${childItemLookup[tempLookup].length}]C)`;
              }
            });
          }

          const childrenSubtotal = `=SUBTOTAL(9, R[1]C:R[${childItemLookup[tempLookup].length}]C)`;
          writeCrossTabCells(row, () => childrenSubtotal, childrenSubtotal);

          // An assembly's own LMSEO values already roll up its children, which are emitted as rows
          // directly below — leaving them as numbers would double-count them in the group subtotal.
          summableColumns.forEach(({ index }) => {
            row[index] = `=SUBTOTAL(9, R[1]C:R[${childItemLookup[tempLookup].length}]C)`;
          });

          // Process and correctly indent child rows
          childItemLookup[tempLookup].forEach((childItem) => {
            const childRow = Array(maxColumnIndex + 1).fill(null);
            Object.keys(columnMap).forEach((key) => {
              // Skip use group and floor group cost per columns - they're handled separately below
              const isUseGroupCostPerColumn = useGroups.some((ug) => key === `${ug.id}_gsf` || key === `${ug.id}_unit`);
              const isFloorGroupCostPerColumn = floorGroups.some((fg) => key === `${fg.id}_gsf`);
              if (!isUseGroupCostPerColumn && !isFloorGroupCostPerColumn && key in childItem) {
                childRow[columnMap[key]] = childItem[key];
              } else if (key in calculatedRangeNames) {
                childRow[columnMap[key]] = calculatedRangeNames[key](childItem);
              }
            });

            // use formulas in total col for child rows
            const unitCostVal = hasUnitCost ? childRow[unitCostColIndex] : null;
            const quantityVal = hasQuantity ? childRow[quantityColIndex] : null;
            const totalVal = hasTotal ? childRow[totalColIndex] : null;

            const totalIsText = typeof totalVal === "string" && totalVal !== "";
            const canSetTotal =
              !totalIsText && hasUnitCost && hasQuantity && hasTotal && unitCostVal != null && quantityVal != null;
            const canSetUnitCost =
              !totalIsText &&
              hasUnitCost &&
              hasTotal &&
              hasQuantity &&
              unitCostVal == null &&
              totalVal != null &&
              quantityVal != null;

            if (canSetTotal) {
              childRow[totalColIndex] = `=IFERROR(RC[${unitCostOffset}]*RC[${quantityOffset}],\"\")`;
            } else if (canSetUnitCost) {
              childRow[unitCostColIndex] =
                `=IFERROR(RC[${-unitCostOffset}]/RC[${quantityColIndex - unitCostColIndex}],\"\")`;
            }

            if (costPerGSFColumnIndex !== undefined && totalColIndex !== undefined) {
              childRow[costPerGSFColumnIndex] = `=IFERROR(RC[${totalColIndex - costPerGSFColumnIndex}]/_gsf,"")`;
            }

            // Add use group totals and cost per columns for assembly items
            if (useGroupsRequested) {
              useGroups.forEach((useGroup) => {
                let useGroupTotal = 0;

                // If the use group belongs to a floor group, calculate proportionally from floor group _$$
                if (useGroup.floor_group && floorGroups.length > 0) {
                  const floorGroup = floorGroups.find((fg) => fg.label === useGroup.floor_group);
                  if (floorGroup) {
                    // Find all use groups in this floor group
                    const useGroupsInFloorGroup = useGroups.filter((ug) => ug.floor_group === floorGroup.label);

                    // Sum all _$ values for use groups in this floor group
                    const totalUseGroupValue = useGroupsInFloorGroup.reduce(
                      (sum, ug) => sum + (Number(childItem[`${ug.name}_$`]) || 0),
                      0
                    );

                    // Calculate this use group's proportion of the floor group
                    const useGroupValue = Number(childItem[`${useGroup.name}_$`]) || 0;
                    const proportion = totalUseGroupValue > 0 ? useGroupValue / totalUseGroupValue : 0;
                    const floorGroupValue = Number(childItem[`${floorGroup.label}_$$`]) || 0;

                    useGroupTotal = floorGroupValue * proportion;
                  } else {
                    // Fallback if floor group not found
                    useGroupTotal = childItem[`${useGroup.name}_$`] || 0;
                  }
                } else {
                  // Use the _$ value directly if no floor group association
                  useGroupTotal = childItem[`${useGroup.name}_$`] || 0;
                }

                childRow[columnMap[useGroup.id]] = useGroupTotal;

                // Add cost per formulas for assembly items
                if (costPerColumnsRequested && costDenominatorsRowIndex !== undefined) {
                  if (columnMap[`${useGroup.id}_gsf`] !== undefined) {
                    childRow[columnMap[`${useGroup.id}_gsf`]] = `=IFERROR(RC[-1]/R${costDenominatorsRowIndex + 1}C,0)`;
                  }
                  if (columnMap[`${useGroup.id}_unit`] !== undefined) {
                    const c = columnMap[`${useGroup.id}_gsf`] !== undefined ? -2 : -1;
                    childRow[columnMap[`${useGroup.id}_unit`]] =
                      `=IFERROR(RC[${c}]/R${costDenominatorsRowIndex + 1}C,0)`;
                  }
                }
              });
            }

            // Add floor group totals and cost per columns for assembly items
            if (floorGroupsRequested) {
              floorGroups.forEach((floorGroup) => {
                // Calculate floor group total from use groups that belong to it
                const useGroupsInFloorGroup = useGroups.filter((ug) => ug.floor_group === floorGroup.label);
                const floorGroupTotal = useGroupsInFloorGroup.reduce(
                  (sum, useGroup) => sum + (Number(childItem[`${useGroup.name}_$`]) || 0),
                  0
                );

                childRow[columnMap[floorGroup.id]] = floorGroupTotal;

                // Add cost per GSF formula for assembly items
                if (costPerColumnsRequested && costDenominatorsRowIndex !== undefined) {
                  if (columnMap[`${floorGroup.id}_gsf`] !== undefined) {
                    childRow[columnMap[`${floorGroup.id}_gsf`]] =
                      `=IFERROR(RC[-1]/R${costDenominatorsRowIndex + 1}C,0)`;
                  }
                }
              });
            }

            writeCrossTabCells(childRow, crossTabItemCost(childItem), crossTabCostPerFormula);

            // Append child row
            childRows.push(childRow);
            formatList.push({
              style: "assemblyItem",
              level: level + 1,
              indentColumn,
              group: Array.from({ length: level + 1 }, (_, i) => i + 1),
            });
          });
        }

        return [row, ...childRows];
      });
    }
    const groupByProp = groupByProps[level];

    const grouped = items.reduce(
      (acc, item) => {
        const blankString = groupByProp === "alternate" ? BLANK_ALTERNATE_LABEL : BLANK_LABEL;
        const groupKey = item[groupByProp] || blankString;
        acc[groupKey] = acc[groupKey] || [];
        acc[groupKey].push(item);
        return acc;
      },
      {} as { [key: string]: LineItem[] }
    );

    const result: (string | number | null)[][] = [];
    const sortedGroupKeys = Object.keys(grouped).sort((a, b) =>
      a === BLANK_LABEL ? 1 : b === BLANK_LABEL ? -1 : a.localeCompare(b)
    );
    sortedGroupKeys.forEach((groupKey, index) => {
      // Create lineage for current group, get the sort_code.code from the first line item in the group
      const mfufRegex = /^(?:mf|uf)\d+$/;
      const groupKeyIsMFUF = mfufRegex.test(groupByProp);
      const groupCode = groupKeyIsMFUF ? `${groupByProp}_code` : groupByProp;
      const firstItem = grouped[groupKey][0];

      const currentLineage = [...lineage, { [groupByProp]: firstItem[groupCode] || groupKey }];

      // Groups without a code (use groups, buildings, the blank group) keep their name as the header.
      // null leaves a cell as printed, so template formulas filled down from the first row survive.
      const headerRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
      const blankString = groupByProp === "alternate" ? BLANK_ALTERNATE_LABEL : BLANK_LABEL;
      const headerCode = firstItem[`${groupByProp}_code`] as string | undefined;
      let headerDisplayValue: string | number | null = headerCode
        ? codeLabel(headerCode, firstItem[`${groupByProp}_desc`] as string | undefined, showCodes)
        : groupKey;
      // When this group is the blank group, show the blank label in header (match subtotal "blank Subtotal")
      if (headerDisplayValue === "" && groupKey === blankString) {
        headerDisplayValue = groupKey;
      }
      headerRow[0] = headerDisplayValue; // headers should always be left-most column

      const { quantity, uom } = lookupOverlineQuantity(overlineQuantitiesMap, currentLineage);
      if (quantityColIndex) headerRow[quantityColIndex] = quantity;
      if (uomColIndex) headerRow[uomColIndex] = uom;
      if (unitCostColIndex && quantityColIndex && totalColIndex) {
        headerRow[unitCostColIndex] =
          `=IFERROR(RC[${totalColIndex - unitCostColIndex}]/RC[${quantityColIndex - unitCostColIndex}],"")`;
      }
      const headerStyle = level === 0 && addBlankRowBetweenGroups ? "sectionHeader" : "header";
      formatList.push({
        style: hideLineItemDetails ? "sovHeader" : headerStyle,
        level,
        indentColumn,
        group: level > 0 ? Array.from({ length: level }, (_, i) => i + 1) : [],
      });

      // Recursively handle nested groups
      const nestedGroup = groupData(grouped[groupKey], level + 1, currentLineage);
      result.push(headerRow, ...nestedGroup);

      if (!onlyLevelOneSubtotals || level === 0) {
        // When hideLineItemDetails consumed the leaf items, nestedGroup is empty so SUBTOTAL formulas
        // would reference a zero-length range. Aggregate directly from the source items instead.
        // With 3+ grouping levels, nestedGroup can be non-empty (deeper headers only) while still having
        // no numeric rows to sum; with onlyLevelOneSubtotals, skipped intermediate subtotals then make
        // SUBTOTAL sum null headers → 0 at level 0 — use direct aggregation there too.
        const isDirectAgg =
          hideLineItemDetails &&
          grouped[groupKey].length > 0 &&
          (nestedGroup.length === 0 || (onlyLevelOneSubtotals && level === 0));

        const getDirectTotal = (items: LineItem[]): number =>
          items.reduce((sum, item) => sum + (Number(item.total) || 0), 0);

        const getDirectColumnTotal = (items: LineItem[], name: string): number =>
          items.reduce(
            (sum, item) => sum + (Number(name in item ? item[name] : calculatedRangeNames[name](item)) || 0),
            0
          );

        // Mirror line-item logic (useGroup / floorGroup columns) so splitByUse + floor_group grouping
        // does not double-count _$ across floor groups when items were not split per use group.
        const splitByFloorGroup = splitByUse && groupByProps.includes("floor_group");

        const getDirectUseGroupTotal = (items: LineItem[], useGroup: ReportUseGroup): number =>
          items.reduce((sum, item) => {
            const cell =
              splitByFloorGroup && useGroup.floor_group !== item.floor_group
                ? 0
                : Number(item[`${useGroup.name}_$`]) || 0;
            return sum + cell;
          }, 0);

        const getDirectFloorGroupTotal = (items: LineItem[], floorGroup: FloorGroup): number =>
          items.reduce((sum, item) => {
            const cell = splitByFloorGroup
              ? floorGroup.label === item.floor_group
                ? Number(item[`${floorGroup.label}_$$`]) || 0
                : 0
              : useGroups
                  .filter((ug) => ug.floor_group === floorGroup.label)
                  .reduce((s, ug) => s + (Number(item[`${ug.name}_$`]) || 0), 0);
            return sum + cell;
          }, 0);

        const getDirectCrossTabTotal = (items: LineItem[], code: string): number =>
          items.reduce((sum, item) => sum + crossTabItemCost(item)(code), 0);

        if (subtotalsInHeader) {
          const itemCount = nestedGroup.length;
          headerRow[columnMap.total] = isDirectAgg
            ? getDirectTotal(grouped[groupKey])
            : `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;

          summableColumns.forEach(({ name, index }) => {
            headerRow[index] = isDirectAgg
              ? getDirectColumnTotal(grouped[groupKey], name)
              : `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;
          });

          if (costPerGSFColumnIndex !== undefined && totalColIndex !== undefined) {
            headerRow[costPerGSFColumnIndex] = `=IFERROR(RC[${totalColIndex - costPerGSFColumnIndex}]/_gsf,"")`;
          }

          if (useGroupsRequested) {
            useGroups.forEach((useGroup) => {
              headerRow[columnMap[useGroup.id]] = isDirectAgg
                ? getDirectUseGroupTotal(grouped[groupKey], useGroup)
                : `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;

              if (costPerColumnsRequested && costDenominatorsRowIndex !== undefined) {
                if (columnMap[`${useGroup.id}_gsf`] !== undefined) {
                  headerRow[columnMap[`${useGroup.id}_gsf`]] = isDirectAgg
                    ? `=IFERROR(RC[-1]/R${costDenominatorsRowIndex + 1}C,0)`
                    : `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;
                }
                if (columnMap[`${useGroup.id}_unit`] !== undefined) {
                  const c = columnMap[`${useGroup.id}_gsf`] !== undefined ? -2 : -1;
                  headerRow[columnMap[`${useGroup.id}_unit`]] = isDirectAgg
                    ? `=IFERROR(RC[${c}]/R${costDenominatorsRowIndex + 1}C,0)`
                    : `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;
                }
              }
            });
          }

          if (floorGroupsRequested) {
            floorGroups.forEach((floorGroup) => {
              headerRow[columnMap[floorGroup.id]] = isDirectAgg
                ? getDirectFloorGroupTotal(grouped[groupKey], floorGroup)
                : `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;

              if (costPerColumnsRequested && costDenominatorsRowIndex !== undefined) {
                if (columnMap[`${floorGroup.id}_gsf`] !== undefined) {
                  headerRow[columnMap[`${floorGroup.id}_gsf`]] = isDirectAgg
                    ? `=IFERROR(RC[-1]/R${costDenominatorsRowIndex + 1}C,0)`
                    : `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;
                }
              }
            });
          }

          writeCrossTabCells(
            headerRow,
            (code) =>
              isDirectAgg ? getDirectCrossTabTotal(grouped[groupKey], code) : `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`,
            isDirectAgg ? crossTabCostPerFormula : `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`
          );
        } else {
          const itemCount = nestedGroup.length;
          const subtotalRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
          subtotalRow[subtotalLabelColumnIndex] = `${headerDisplayValue} Subtotal`;
          subtotalRow[columnMap.total] = isDirectAgg
            ? getDirectTotal(grouped[groupKey])
            : `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;

          summableColumns.forEach(({ name, index }) => {
            subtotalRow[index] = isDirectAgg
              ? getDirectColumnTotal(grouped[groupKey], name)
              : `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;
          });

          if (costPerGSFColumnIndex !== undefined && totalColIndex !== undefined) {
            subtotalRow[costPerGSFColumnIndex] = `=IFERROR(RC[${totalColIndex - costPerGSFColumnIndex}]/_gsf,"")`;
          }

          if (useGroupsRequested) {
            useGroups.forEach((useGroup) => {
              subtotalRow[columnMap[useGroup.id]] = isDirectAgg
                ? getDirectUseGroupTotal(grouped[groupKey], useGroup)
                : `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;

              if (costPerColumnsRequested && costDenominatorsRowIndex !== undefined) {
                if (columnMap[`${useGroup.id}_gsf`] !== undefined) {
                  subtotalRow[columnMap[`${useGroup.id}_gsf`]] = isDirectAgg
                    ? `=IFERROR(RC[-1]/R${costDenominatorsRowIndex + 1}C,0)`
                    : `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;
                }
                if (columnMap[`${useGroup.id}_unit`] !== undefined) {
                  const c = columnMap[`${useGroup.id}_gsf`] !== undefined ? -2 : -1;
                  subtotalRow[columnMap[`${useGroup.id}_unit`]] = isDirectAgg
                    ? `=IFERROR(RC[${c}]/R${costDenominatorsRowIndex + 1}C,0)`
                    : `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;
                }
              }
            });
          }

          if (floorGroupsRequested) {
            floorGroups.forEach((floorGroup) => {
              subtotalRow[columnMap[floorGroup.id]] = isDirectAgg
                ? getDirectFloorGroupTotal(grouped[groupKey], floorGroup)
                : `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;

              if (costPerColumnsRequested && costDenominatorsRowIndex !== undefined) {
                if (columnMap[`${floorGroup.id}_gsf`] !== undefined) {
                  subtotalRow[columnMap[`${floorGroup.id}_gsf`]] = isDirectAgg
                    ? `=IFERROR(RC[-1]/R${costDenominatorsRowIndex + 1}C,0)`
                    : `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;
                }
              }
            });
          }

          writeCrossTabCells(
            subtotalRow,
            (code) =>
              isDirectAgg ? getDirectCrossTabTotal(grouped[groupKey], code) : `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`,
            isDirectAgg ? crossTabCostPerFormula : `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`
          );

          result.push(subtotalRow);
          formatList.push({
            style: "subtotal",
            level,
            group: Array.from({ length: level + 1 }, (_, i) => i + 1),
            cellOverrides: [{ columnIndex: subtotalLabelColumnIndex, horizontalAlignment: "Right" }],
          });
        }
      }

      // Add a blank row between top-level groups, if enabled. "" wipes the formulas filled down from
      // the first row, which would otherwise show a phantom subtotal on the spacer.
      if (addBlankRowBetweenGroups && level === 0 && index < sortedGroupKeys.length - 1) {
        const blankRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill("");
        result.push(blankRow);
        formatList.push({
          style: "spacer",
          level,
          indentColumn,
          group: level > 0 ? Array.from({ length: level }, (_, i) => i + 1) : [],
        });
      }
    });

    return result;
  };

  // Start grouping from level 0
  const dataArray = groupData(processedData, 0, []);
  return { dataArray, formatList };
};
